import React, { useState, useMemo } from 'react';
import { AgeBucket, AgeBucketId, CriteriaKey, SepsisState } from './types';
import { useSepsisScore } from './hooks/useSepsisScore';
import CriteriaGrid from './components/CriteriaGrid';
import ScoreBanner from './components/ScoreBanner';
import RiskFactorsModal from './components/RiskFactorsModal';
import ThresholdsCard from './components/ThresholdsCard';
import { evalTemp, evalHR, evalBP } from './utils/thresholdEval';
import thresholds from './data/sepsisThresholds.json';
import './SepsisApp.scss';

const AUTO_CRITERIA: CriteriaKey[] = ['temperature', 'tachycardia', 'lowBP'];

const INPUT_FOR_CRITERIA: Record<string, keyof SepsisState> = {
  temperature: 'temperature',
  tachycardia: 'heartRate',
  lowBP: 'bloodPressure',
};

const INITIAL_MANUAL_TOGGLES: Record<CriteriaKey, boolean> = {
  temperature: false,
  tachycardia: false,
  lowBP: false,
  consciousness: false,
  capillaryRefill: false,
  weakPulse: false,
  mottledSkin: false,
  riskFactor: false,
};

const SepsisApp: React.FC = () => {
  // --- Input state (strings to allow empty) ---
  const [ageInput, setAgeInput] = useState<AgeBucketId | null>(null);
  const [tempInput, setTempInput] = useState<string>('');
  const [hrInput, setHrInput] = useState<string>('');
  const [bpInput, setBpInput] = useState<string>('');

  // --- Manual toggles ---
  const [manualToggles, setManualToggles] = useState<Record<CriteriaKey, boolean>>(
    { ...INITIAL_MANUAL_TOGGLES }
  );

  // --- Modal visibility ---
  const [riskInfoOpen, setRiskInfoOpen] = useState(false);

  // --- Build SepsisState for hook ---
  const sepsisState: SepsisState = {
    age: ageInput,
    temperature: tempInput !== '' ? parseFloat(tempInput) : null,
    heartRate: hrInput !== '' ? parseInt(hrInput, 10) : null,
    bloodPressure: bpInput !== '' ? parseInt(bpInput, 10) : null,
    manualToggles,
  };

  const { activeButtons, score, isHighRisk } = useSepsisScore(sepsisState);

  const ageBucketMap = useMemo(() => {
    const map = new Map<string, AgeBucket>();
    (thresholds.ageBuckets as AgeBucket[]).forEach((b) => map.set(b.id, b));
    return map;
  }, []);

  // --- Handlers ---
  const handleToggle = (key: CriteriaKey) => {
    setManualToggles((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const syncBox = (key: 'temperature' | 'heartRate' | 'bloodPressure', val: string) => {
    const bucket = ageInput ? ageBucketMap.get(ageInput) : null;
    
    setManualToggles(prev => {
      const next = { ...prev };
      if (key === 'temperature') {
        next.temperature = evalTemp(val !== '' ? parseFloat(val) : null, bucket ?? null);
      }
      if (key === 'heartRate') {
        next.tachycardia = evalHR(val !== '' ? parseInt(val, 10) : null, bucket ?? null);
      }
      if (key === 'bloodPressure') {
        next.lowBP = evalBP(val !== '' ? parseInt(val, 10) : null, bucket ?? null);
      }
      return next;
    });
  };

  const handleAgeChange = (val: AgeBucketId | null) => {
    setAgeInput(val);
  };

  const handleTempChange = (val: string) => {
    if (val === tempInput) return;
    setTempInput(val);
    syncBox('temperature', val);
  };

  const handleHRChange = (val: string) => {
    if (val === hrInput) return;
    setHrInput(val);
    syncBox('heartRate', val);
  };

  const handleBPChange = (val: string) => {
    if (val === bpInput) return;
    setBpInput(val);
    syncBox('bloodPressure', val);
  };

  const handleReset = () => {
    setAgeInput(null);
    setTempInput('');
    setHrInput('');
    setBpInput('');
    setManualToggles({ ...INITIAL_MANUAL_TOGGLES });
  };

  return (
    <div className="sepsis-app" dir="rtl" data-testid="sepsis-app">
      {/* Page header */}
      <div className="sepsis-app__header">
        <h1 className="sepsis-app__title m-0 tracking-[-0.5px]">
          סקר ספסיס בילדים
        </h1>
      </div>

      {/* Criteria + thresholds card — side by side on desktop, stacked on mobile */}
      <div className="sepsis-app__main">
        <ThresholdsCard
          selectedAgeBucketId={sepsisState.age}
          onAgeChange={handleAgeChange}
          temperature={sepsisState.temperature}
          heartRate={sepsisState.heartRate}
          bloodPressure={sepsisState.bloodPressure}
        />
        <CriteriaGrid
          activeButtons={activeButtons}
          onToggle={handleToggle}
          onRiskInfoClick={() => setRiskInfoOpen(true)}
        />
      </div>

      {/* Inline score / alert banner — below main row */}
      <ScoreBanner score={score} isHighRisk={isHighRisk} />

      {/* Workflow action buttons */}
      <div className="sepsis-app__workflow-actions">
        <button
          type="button"
          className={`sepsis-app__workflow-btn sepsis-app__workflow-btn--${isHighRisk ? 'protocol' : 'next'}`}
          onClick={handleReset}
          data-testid="sepsis-workflow-btn"
        >
          {isHighRisk ? 'סימנתי חשד לספסיס במערכת' : 'סימנתי ללא חשד במערכת'}
        </button>
      </div>

      {/* Modals */}
      <RiskFactorsModal
        open={riskInfoOpen}
        onClose={() => setRiskInfoOpen(false)}
      />
    </div>
  );
};

export default SepsisApp;
